<template>
  <div class="basic-information">
    <p class="top-title">
      {{ $t('company.basic.basicInfo') }}
    </p>
    <div class="information">
      <InputField
        :class="`${editable ? 'small-8 medium-8 large-8' : 'small-12 medium-12 large-12'}`"
        :readonly="!editable"
        :label="$t('user.tenantName')"
        placeholder=""
        :showFlag="false"
        :showColon="false"
        :hideLabel="false"
        :readonlyAsInput="editable"
        :value="companyName"
      />
      <div class="project-location">
        <label
          for="projectLocation"
          :class="[{'required-flag': editable}]"
        >
          {{ $t('tender.general.premises') }}
        </label>
        <InputField
          v-if="editable"
          :value="displayLocation"
          :placeholder="$t('tender.general.premisesHint')"
          :showFlag="false"
          :showColon="false"
          :hideLabel="false"
          required
          :readonlyAsInput="isReadonly"
          @updateValue="val => changeInputLocation(val)"
        />
        <span v-else>{{ displayLocation }}</span>
      </div>
      <div class="floors">
        <label
          for="floors"
          :class="[{'required-flag': editable}]"
        >
          {{ $t('tenancy.floors') }}
        </label>
        <HKLMultiSelect
          v-if="editable"
          :class="{'error-highlight': !multipleFloorValidity}"
          :options="floorsHklOptions"
          :value="floors"
          :showSearch="true"
          placeholder=""
          :disabled="isReadonly"
          @change="val => changeInput('floors', val || [])"
        />
        <span v-else>{{ displayFloors }}</span>
      </div>
      <div class="type">
        <label
          for="type"
          :class="[{'required-flag': editable}]"
        >
          {{ $t('tenancy.type') }}
        </label>
        <HKLSelect
          v-if="editable"
          :disabled="!isDraftProject"
          :options="typeHklOptions"
          :value="type || undefined"
          :required="true"
          :showSearch="true"
          placeholder=""
          @change="val => changeInput('type', val)"
        />
        <pre v-else>{{ displayTypeStuff }}</pre>
        <div
          v-show="editable"
          class="project-type-radio"
        >
          <input
            v-model="typeSubsidiary"
            type="radio"
            :value="typeSubsidiaryMap.businessAsUsual"
            @change="e => changeInput('typeSubsidiary', e.currentTarget.value)"
          >
          <label :for="typeSubsidiaryMap.businessAsUsual">{{ typeSubsidiaryMap.businessAsUsual | customValueByKey({obj: typeSubsidiaryMap, rootPath: 'tenancy'}) }}</label>
          <input
            v-model="typeSubsidiary"
            type="radio"
            :value="typeSubsidiaryMap.temporarySuspension"
            @change="e => changeInput('typeSubsidiary', e.currentTarget.value)"
          >
          <label :for="typeSubsidiaryMap.temporarySuspension">{{ typeSubsidiaryMap.temporarySuspension | customValueByKey({obj: typeSubsidiaryMap, rootPath: 'tenancy'}) }}</label>
        </div>
        <InputField
          v-if="editable"
          class="small-10 medium-10 large-10"
          label=""
          placeholder=""
          :showColon="false"
          :hideLabel="true"
          :value="typeRemarks"
          @updateValue="val => changeInput('typeRemarks', val)"
        />
      </div>
      <InputField
        :readonly="!editable"
        class="small-10 medium-10 large-10 project-description"
        :label="$t('tender.general.projectDescription')"
        placeholder=""
        :showColon="false"
        :hideLabel="false"
        :showFlag="editable"
        :value="displayDescription"
        required
        @updateValue="val => changeInput('description', val)"
      />
      <div class="tentative-date">
        <label
          for="tentative-date"
          :class="[{'required-flag': editable}]"
        >
          {{ $t('tenancy.tentativeDateofWorkCommencement') }}
        </label>
        <DatePicker
          v-if="editable"
          :calendarButton="true"
          :value="tentativeDate"
          calendarButtonIcon="fa fa-calendar"
          :clearButton="true"
          :disabled="{to: new Date(Date.now() - dateUtil.ONE_DAY_MILLI)}"
          required
          @selected="val => changeInput('tentativeDate', val)"
        />
        <span v-else>{{ displayTentativeDate }}</span>
      </div>
      <div class="estimatedDuration">
        <InputField
          class="small-4 medium-4 large-4"
          :readonly="!editable"
          :label="$t('tender.general.estimatedDuration')"
          placeholder=""
          :showColon="false"
          :showFlag="editable"
          :value="editable ? estimatedDuration : `${estimatedDuration || '--'} ${$t('tender.general.days')}`"
          :pattern="regex.integer"
          required
          @updateValue="val => changeEstimatedDuration(val)"
        />
        <span
          v-if="editable"
          class="days"
        >
          {{ $t('tender.general.days') }}
        </span>
      </div>
      <div
        v-if="estimatedDuration"
        class="tentative-end-date"
      >
        {{ $t('tenancy.tentativeEndDate', [tentativeEndDate]) }}
      </div>
    </div>
  </div>
</template>

<script>
import lodash from 'lodash'
import moment from 'moment'
import { mapGetters, mapMutations } from 'vuex'
import { getCurrentInstance } from 'vue-demi'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import InputField from '@/controls/InputField'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import DatePicker from '@/controls/DatePicker'
import mutationTypes from '@/store/mutation-types'
import * as regex from '@/utils/regex'
import dateUtil from '@/utils/dateUtil'
import { tenantProjectStatus, tenantProjectTypes, typeSubsidiaryMap } from '@/utils/locale'

export default {
  name: 'ProjectBasicInformation',
  components: {
    InputField,
    HKLSelect,
    HKLMultiSelect,
    DatePicker,
  },
  props: {
    editable: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const { proxy } = getCurrentInstance()
    const changeEstimatedDuration = (val) => {
      const parsedVal = parseInt(val)
      const v = Number.isInteger(parsedVal) ? parsedVal : null
      proxy.changeInput('estimatedDuration', v)
    }
    return {
      changeEstimatedDuration,
    }
  },
  data () {
    return {
      regex,
      multipleFloorValidity: true,
      multipleLocationsValidity: true,
      premisesOptions: [],
      floorsOptions: [],
      dateUtil,
      typeSubsidiaryMap,
    }
  },
  computed: {
    ...mapGetters('tenantProject', ['currentTenantProject']),
    ...mapGetters('ccd', ['premisesDetailList']),
    ...mapGetters('user', ['currentUser', 'isTenant']),
    typeOptions () {
      if (this.isTenant) {
        return [tenantProjectTypes.Fitout]
      }
      return [tenantProjectTypes.Fitout, tenantProjectTypes.Reinstatement]
    },
    floorsHklOptions () {
      return (this.floorsOptions || []).map((floor) => ({ name: floor, value: floor }))
    },
    typeHklOptions () {
      return this.typeOptions.map((option) => ({
        name: this.$t(`tenancyProject.${lodash.toLower(option)}`),
        value: option,
      }))
    },
    companyName () {
      return lodash.get(this.currentTenantProject, 'tenancy.company.name', '')
    },
    projectLocation () {
      const locations = lodash.get(this.currentTenantProject, 'projectLocation') || []
      return locations
    },
    estimatedDuration () {
      return lodash.get(this.currentTenantProject, 'estimatedDuration', '')
    },
    tentativeEndDate () {
      return dateUtil.formatDate(moment(this.tentativeDate).add(this.estimatedDuration, 'days'))
    },
    description () {
      return lodash.get(this.currentTenantProject, 'description', '')
    },
    type () {
      return lodash.get(this.currentTenantProject, 'type', '')
    },
    tentativeDate () {
      const tentativeDate = lodash.get(this.currentTenantProject, 'tentativeDate', '')
      return tentativeDate ? new Date(tentativeDate) : ''
    },
    typeRemarks () {
      return lodash.get(this.currentTenantProject, 'typeRemarks', '')
    },
    floors () {
      return lodash.get(this.currentTenantProject, 'floors', [])
    },
    typeSubsidiary () {
      return lodash.get(this.currentTenantProject, 'typeSubsidiary', '')
    },

    displayFloors () {
      return this.floors.join(', ') || '--'
    },
    displayTypeStuff () {
      const typeSubsidiaryValue = this.$options.filters.customValueByKey(this.typeSubsidiary, {obj: this.typeSubsidiaryMap, rootPath: 'tenancy'})
      const type = this.type ? this.$t(`tenancyProject.${lodash.toLower(this.type)}`) : '--'
      return `${type} \n${typeSubsidiaryValue} \n${this.typeRemarks || ''}`
    },
    displayTentativeDate () {
      return dateUtil.format(this.tentativeDate, 'DD MMM YY') || '--'
    },
    displayProjectLocation () {
      return !this.editable && !lodash.isEmpty(this.projectLocation) ? '--' : this.projectLocation
    },
    displayDescription () {
      return !this.editable && !this.description ? '--' : this.description
    },
    isDraftProject () {
      return this.currentTenantProject.status === tenantProjectStatus.DRAFT
    },
    isReadonly () {
      return !(!this.isTenant || this.currentTenantProject.status === tenantProjectStatus.DRAFT)
    },
    // @TODO <Kian_Zhou@EPAM.COM> the type of projectLocation should be changed to string in future
    displayLocation () {
      return this.projectLocation[0]
    },
  },
  watch: {
    premisesDetailList () {
      this.initOptions()
    },
    editable (val) {
      if (val) {
        this.$nextTick(() => this.preselectMandatorySingleOptionFields())
      }
    },
  },
  mounted () {
    this.initOptions()
  },
  methods: {
    ...mapMutations('tenantProject', {
      updateFieldByPath: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_PATH,
      updateFieldByName: mutationTypes.TENANT_PROJECT_UPDATE_FIELD_NAME,
    }),
    changeInput (name, value) {
      this.updateFieldByName({name, value})
    },
    changeInputLocation (value) {
      this.changeInput('projectLocation', [value])
    },
    addFloors (floors) {
      const newFloors = lodash.cloneDeep(this.floors).concat(floors)
      this.changeInput('floors', lodash.uniq(newFloors))
    },
    validate () {
      formUtils.resetDirty()
      fusionInputCheckUtil.resetAllFields()
      this.resetFloorsValidation()
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.basic-information')
      this.floorsValidate()
      return fusionInputCheckUtil.isAllFieldsValid('.basic-information') && this.multipleFloorValidity && this.multipleLocationsValidity
    },
    resetValidation () {
      this.resetFloorsValidation()
      formUtils.resetDirty()
      fusionInputCheckUtil.resetAllFields()
    },
    resetFloorsValidation () {
      this.multipleFloorValidity = true
      this.multipleLocationsValidity = true
    },
    floorsValidate () {
      if (lodash.isEmpty(this.floors)) {
        this.multipleFloorValidity = false
      }
      if (lodash.isEmpty(this.displayLocation)) {
        this.multipleLocationsValidity = false
      }
    },
    initOptions () {
      const floors = []
      const details = []
      this.premisesDetailList.map(item => {
        if (dateUtil.isBeforeEndDate(item.endDate)) {
          floors.push(item.floor)
          details.push(item.detail)
        }
      })
      this.floorsOptions = lodash.uniq(floors)
      this.premisesOptions = lodash.uniq(details)
      this.$nextTick(() => this.preselectMandatorySingleOptionFields())
    },
    preselectMandatorySingleOptionFields () {
      if (!this.editable) return

      if (this.floorsOptions.length === 1 && lodash.isEmpty(this.floors)) {
        this.changeInput('floors', [this.floorsOptions[0]])
      }

      if (
        this.typeOptions.length === 1 &&
        lodash.isEmpty(this.type) &&
        this.isDraftProject
      ) {
        this.changeInput('type', this.typeOptions[0])
      }
    },
  },
}
</script>

<style lang="scss">
@import '~styles/multi-value';

.basic-information {
  padding: 20px 15px 20px 16px;
  background: #fafafa;
  .top-title {
    font-size: 16px;
    font-weight: bold;
  }
  label {
    margin-top: 0 !important;
    margin-bottom: 5px;
    font-size: 14px;
    line-height: 1.43;
    font-weight: bold;
    color: #0C3A58;
  }
  .tentative-date,
  .project-description,
  .floors,
  .project-location,
  .type {
    margin-bottom: 15px;
  }
  .project-location {
    margin-top: 15px;
    .input-field {
      width: 50%;
    }
  }
  .type .hkl-select {
    width: 172px;
  }
  .floors {
    min-width: 132px;
    .hkl-select {
      width: 36%;
    }
  }
  .tentative-date {
    .vdp-datepicker {
      width: 172px;
    }
  }
  .project-type-radio {
    input[type='radio'] {
      margin: 0.5rem 0,
    }
    label {
      font-weight: normal;
    }
  }
  .estimatedDuration {
    .input-field {
      display: inline-block;
      vertical-align: bottom;
    }
  }
  .days {
    font-weight: bold;
    color: #868686;
    display: inline-block;
    height: 40px;;
    line-height: 40px;
  }
  .tentative-end-date {
    color: #878787;
    font-style: italic;
    font-size: 12px;
    margin-top: 0.5rem;
  }
  .text-input input {
    margin-bottom: 0 !important;
  }
  input.date-picker-class {
    width: 100%;
  }

  .floors,
  .project-location{
    vertical-align: top;
    p {
      margin: 0;
    }
    .search-title {
      font-size: 14px;
      font-weight: bold;
      height: 27px;
      line-height: 27px;
      display: inline-block;
    }
    .hkl-select {
      min-height: 40px;
    }
  }
}
.required-flag:after {
  color: #E49405;
  content: '*';
}
</style>
